<template>
  <div class="add-plant-page">
    <div class="page-header">
      <h1>🌱 添加植物信息</h1>
      <p>填写植物的基本资料、养护环境与繁殖方式</p>
    </div>

    <el-card class="form-wrapper" shadow="always">
      <el-form
          ref="formRef"
          :model="plantForm"
          :rules="rules"
          label-width="120px"
          label-position="left"
          class="plant-form"
      >
        <!-- 基本信息 -->
        <el-divider>基本信息</el-divider>
        <div class="form-section">

          <el-form-item label="植物名称" prop="name">
            <el-input v-model="plantForm.name" placeholder="请输入植物名称" />
          </el-form-item>

          <el-form-item label="拉丁名">
            <el-input v-model="plantForm.latin_name" placeholder="请输入拉丁名" />
          </el-form-item>

          <el-form-item label="花语">
            <el-input v-model="plantForm.passionate_love" placeholder="请输入花语" />
          </el-form-item>

          <el-form-item label="科属">
            <el-input v-model="plantForm.family" placeholder="如：菊科、蔷薇科" />
          </el-form-item>

          <el-form-item label="简介">
            <el-input
                type="textarea"
                :rows="3"
                v-model="plantForm.intro"
                placeholder="请输入植物简介"
            />
          </el-form-item>

          <el-form-item label="热门植物">
            <el-radio-group v-model="plantForm.hot_plants">
              <el-radio :label="true">是</el-radio>
              <el-radio :label="false">否</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="展示花语祈福">
            <el-radio-group v-model="plantForm.is_show">
              <el-radio :label="true">是</el-radio>
              <el-radio :label="false">否</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="上传图片" prop="image">
            <div class="upload-field">
              <el-upload
                  class="upload-box"
                  :action="uploadImageApi"
                  :on-success="handleUploadSuccess"
                  :on-error="handleUploadError"
                  :limit="1"
                  :show-file-list="false"
              >
                <el-button type="primary">选择图片</el-button>
              </el-upload>

              <div v-if="plantForm.image" class="preview-thumb">
                <img :src="plantForm.image" alt="预览" />
              </div>
            </div>
          </el-form-item>

        </div>

        <!-- 生长环境 -->
        <el-divider>生长环境</el-divider>
        <div class="form-section">

          <el-form-item label="土壤">
            <el-input
                type="textarea"
                :rows="3"
                v-model="plantForm.soil"
                placeholder="请输入土壤要求"
            />
          </el-form-item>

          <el-form-item label="温度">
            <el-input
                type="textarea"
                :rows="3"
                v-model="plantForm.temperature"
                placeholder="适宜温度范围"
            />
          </el-form-item>

          <el-form-item label="光照">
            <el-input
                type="textarea"
                :rows="3"
                v-model="plantForm.light"
                placeholder="光照需求"
            />
          </el-form-item>

          <el-form-item label="浇水">
            <el-input
                type="textarea"
                :rows="3"
                v-model="plantForm.watering"
                placeholder="浇水方法"
            />
          </el-form-item>
        </div>

        <!-- 繁殖与修剪 -->
        <el-divider>繁殖 / 修剪</el-divider>
        <div class="form-section">

          <el-form-item label="繁殖">
            <el-input
                type="textarea"
                :rows="3"
                v-model="plantForm.propagation"
                placeholder="播种 / 扦插 / 分株等繁殖方式"
            />
          </el-form-item>

          <el-form-item label="修剪">
            <el-input
                type="textarea"
                :rows="3"
                v-model="plantForm.pruning"
                placeholder="修剪方法"
            />
          </el-form-item>

        </div>

        <!-- 提交按钮 -->
        <div class="form-actions">
          <el-button type="primary" :loading="submitting" @click="submitForm">提交</el-button>
          <el-button @click="resetForm">重置</el-button>
        </div>

      </el-form>
    </el-card>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { v4 as uuidv4 } from 'uuid'
const router = useRouter()
import api, { API_BASE_URL } from '@/services/api'
import {useRouter} from "vue-router";

const formRef = ref(null)
const submitting = ref(false)

const initialUuid = uuidv4()

const plantForm = reactive({
  id: initialUuid,
  name: '',
  latin_name: '',
  passionate_love: '',
  image: '',
  family: '',
  intro: '',
  hot_plants: false,
  is_show: false,
  soil: '',
  temperature: '',
  light: '',
  watering: '',
  propagation: '',
  pruning: ''
})

const rules = {
  name: [{ required: true, message: '请输入植物名称', trigger: 'blur' }],
  image: [{ required: true, message: '请上传植物图片', trigger: 'change' }]
}

// 计算上传地址
const uploadImageApi = computed(() => {
  if (!plantForm.id) return ''
  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL
  return `${baseUrl}/upload/image/${plantForm.id}`
})

const handleUploadSuccess = (res) => {
  if (res.url) {
    plantForm.image = res.url
    ElMessage.success('图片上传成功')
  }
}

const handleUploadError = () => {
  ElMessage.error('图片上传失败')
}

// 提交
const submitForm = () => {
  formRef.value.validate(async (valid) => {
    if (!valid) return

    submitting.value = true
    try {
      const payload = { ...plantForm }

      const res = await api.post('/plants/add', payload)

      ElMessage.success('植物添加成功')
      resetForm()
      router.push('/baike')
    } catch (err) {
      ElMessage.error('提交失败')
      console.error(err)
    } finally {
      submitting.value = false
    }
  })
}

// 重置
const resetForm = () => {
  formRef.value.resetFields()
  const newID = uuidv4()
  plantForm.id = newID
  plantForm.image = ''
}
</script>

<style scoped>
.add-plant-page {
  background: #f5f7fa;
  min-height: 100vh;
  padding: 40px 60px;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
}

.form-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 60px;
  border-radius: 16px;
}

.plant-form {
  max-width: 650px;
  margin: 0 auto;
}

.upload-field {
  display: flex;
  align-items: center;
  gap: 20px;
}

.preview-thumb {
  width: 120px;
  height: 90px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #ddd;
}

.preview-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.form-actions {
  text-align: center;
  margin-top: 40px;
}
</style>
